import { useMemo, useState } from 'react';
import { motion } from 'motion/react';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, CalendarClock, CreditCard, Eye, Flag, Gavel, Inbox, MailWarning, RefreshCw, Reply, Search, Sparkles, Zap } from 'lucide-react';
import { displayName, parseMailbox, type BriefPriority } from '@dts/core';
import { api, errorMessage } from '@/lib/api';
import { qk, useMessages, useFolders, useMe } from '@/lib/queries';
import { useUi } from '@/lib/store';
import { composeReply, composeWithAi } from '@/lib/compose';
import { greeting, listTime, CATEGORY_META } from '@/lib/format';
import { Avatar } from '@/components/Avatar';
import { Button, cn, Num, tint } from '@/components/ui';

const ACTION_ICON: Record<BriefPriority['action'], React.ReactNode> = {
  reply: <Reply />, read: <Eye />, schedule: <CalendarClock />, pay: <CreditCard />, review: <Search />, decide: <Gavel />,
};

export function Today() {
  const me = useMe().data;
  const folders = useFolders().data ?? [];
  const setView = useUi((s) => s.setView);
  const select = useUi((s) => s.select);
  const openAsk = useUi((s) => s.openAsk);
  const inbox = useMessages({ kind: 'mail', folder: 'Inbox' });
  const brief = useQuery({ queryKey: qk.brief, queryFn: api.brief, staleTime: 10 * 60_000, retry: 0 });
  const [ask, setAsk] = useState('');

  const items = useMemo(() => inbox.data?.pages.flatMap((p) => p.items) ?? [], [inbox.data]);
  const byId = useMemo(() => new Map(items.map((m) => [m.id, m])), [items]);
  const needsReply = items.filter((m) => m.aiCategory === 'needs_reply' && m.direction !== 'outbound').slice(0, 6);
  const unread = folders.find((f) => f.name === 'Inbox')?.unread ?? 0;
  const flagged = items.filter((m) => m.isFlagged).length;
  const today = items.filter((m) => new Date(m.receivedUtc).toDateString() === new Date().toDateString()).length;
  const name = brief.data?.firstName ?? me?.user.displayName.split(' ')[0] ?? '';

  const open = (id: string) => {
    setView({ kind: 'mail', folder: 'Inbox' });
    select(id);
  };
  const replyTo = async (id: string, instruction?: string) => {
    const d = await api.getMessage(id);
    composeReply(d, false, instruction ?? 'Write the most helpful natural reply.');
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-[1080px] px-10 pb-16 pt-6">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mb-2 text-[13px] font-medium text-muted">
          {new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
        </motion.div>
        <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="font-display text-[56px] leading-[1.02] tracking-tight">
          {greeting()}{name ? `, ${name}` : ''}.
        </motion.h1>

        {/* AI headline + digest */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }} className={cn('ai-ring mt-7 rounded-3xl bg-raised/60 p-7 backdrop-blur', brief.isFetching && 'busy')}>
          <div className="mb-3 flex items-center gap-2">
            <span className="flex size-6 items-center justify-center rounded-lg ai-gradient text-white"><Sparkles className="size-3.5" /></span>
            <span className="text-[11.5px] font-semibold uppercase tracking-[0.1em] ai-text">Your brief</span>
            <span className="flex-1" />
            <button onClick={() => brief.refetch()} className="flex items-center gap-1.5 text-[12px] text-muted hover:text-fg">
              <RefreshCw className={cn('size-3.5', brief.isFetching && 'animate-spin')} /> Refresh
            </button>
          </div>
          {brief.isLoading ? (
            <div className="space-y-3">
              <div className="shimmer-text text-[22px] font-medium">Reading your inbox…</div>
              <div className="skeleton h-3.5 w-11/12" />
              <div className="skeleton h-3.5 w-3/4" />
            </div>
          ) : brief.isError ? (
            <div className="text-[13.5px] text-danger">Couldn't build the brief: {errorMessage(brief.error)}</div>
          ) : brief.data ? (
            <>
              <div className="selectable text-[23px] font-semibold leading-snug tracking-tight">{brief.data.headline}</div>
              {brief.data.digest && <p className="selectable mt-3 max-w-3xl text-[14.5px] leading-relaxed text-fg/80">{brief.data.digest.replace(/\*\*/g, '')}</p>}
            </>
          ) : null}
        </motion.div>

        {/* stats */}
        <div className="mt-5 grid grid-cols-4 gap-3">
          {[
            { label: 'Unread', value: unread, icon: <Inbox />, onClick: () => setView({ kind: 'mail', folder: 'Inbox', filter: 'unread' }) },
            { label: 'Need your reply', value: items.filter((m) => m.aiCategory === 'needs_reply').length, icon: <MailWarning />, onClick: () => setView({ kind: 'mail', folder: 'Inbox', filter: 'needs_reply' }), hot: true },
            { label: 'Flagged', value: flagged, icon: <Flag />, onClick: () => setView({ kind: 'mail', filter: 'flagged' }) },
            { label: 'Arrived today', value: today, icon: <Zap />, onClick: () => setView({ kind: 'mail', folder: 'Inbox' }) },
          ].map((s, i) => (
            <motion.button
              key={s.label}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.18 + i * 0.04 }}
              onClick={s.onClick}
              className="glass group rounded-2xl p-4 text-left transition hover:-translate-y-0.5 hover:border-line-strong"
            >
              <div className={cn('mb-3 flex size-8 items-center justify-center rounded-xl [&_svg]:size-4', s.hot ? 'ai-gradient text-white' : 'bg-line text-muted')}>{s.icon}</div>
              <div className="text-[28px] font-semibold tabular-nums tracking-tight"><Num>{s.value}</Num></div>
              <div className="legend text-[12px] text-muted">{s.label}</div>
            </motion.button>
          ))}
        </div>

        <div className="mt-8 grid grid-cols-5 gap-6">
          {/* priorities */}
          <div className="col-span-3">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-[15px] font-semibold">Top priorities</h2>
              <span className="text-[12px] text-faint">ranked by AI</span>
            </div>
            <div className="space-y-2.5">
              {brief.isLoading &&
                Array.from({ length: 3 }).map((_, i) => <div key={i} className="skeleton h-[76px] rounded-2xl" />)}
              {brief.data?.priorities.length === 0 && <div className="glass rounded-2xl p-6 text-center text-[13px] text-muted">Nothing urgent. Enjoy the calm.</div>}
              {brief.data?.priorities.map((p, i) => {
                const m = byId.get(p.id);
                const from = m ? parseMailbox(m.from) ?? { address: m.fromAddress } : null;
                return (
                  <motion.div
                    key={p.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.25 + i * 0.06 }}
                    className="glass group flex items-center gap-4 rounded-2xl p-4 transition hover:border-line-strong"
                  >
                    <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-line text-[13px] font-bold tabular-nums text-muted">{i + 1}</div>
                    {from && <Avatar name={displayName(from)} address={from.address} size={36} />}
                    <button className="min-w-0 flex-1 text-left" onClick={() => open(p.id)}>
                      <div className="truncate text-[14px] font-semibold">{p.title}</div>
                      <div className="truncate text-[12.5px] text-muted">{p.why}</div>
                    </button>
                    {p.action === 'reply' ? (
                      <Button size="sm" variant="ai" icon={<Sparkles className="size-3.5" />} onClick={() => void replyTo(p.id)}>Draft reply</Button>
                    ) : (
                      <Button size="sm" variant="soft" icon={<span className="[&_svg]:size-3.5">{ACTION_ICON[p.action]}</span>} onClick={() => open(p.id)} className="capitalize">{p.action}</Button>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* needs reply */}
          <div className="col-span-2">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-[15px] font-semibold">Waiting on you</h2>
              <button className="flex items-center gap-1 text-[12px] text-muted hover:text-fg" onClick={() => setView({ kind: 'mail', folder: 'Inbox', filter: 'needs_reply' })}>
                All <ArrowRight className="size-3" />
              </button>
            </div>
            <div className="glass divide-y divide-line overflow-hidden rounded-2xl">
              {needsReply.length === 0 && <div className="p-6 text-center text-[13px] text-muted">You're all caught up.</div>}
              {needsReply.map((m) => {
                const from = parseMailbox(m.from) ?? { address: m.fromAddress };
                return (
                  <div key={m.id} className="group flex items-center gap-3 px-4 py-3 transition hover:bg-line/60">
                    <Avatar name={displayName(from)} address={from.address} size={30} />
                    <button className="min-w-0 flex-1 text-left" onClick={() => open(m.id)}>
                      <div className="flex items-baseline gap-2">
                        <span className="truncate text-[13px] font-semibold">{displayName(from)}</span>
                        <span className="ml-auto shrink-0 text-[11px] text-faint"><Num>{listTime(m.receivedUtc)}</Num></span>
                      </div>
                      <div className="truncate text-[12px] text-muted">{m.aiGist ?? m.subject}</div>
                    </button>
                    <button onClick={() => void replyTo(m.id)} title="Draft a reply with AI" className="flex size-8 shrink-0 items-center justify-center rounded-lg text-accent opacity-0 transition hover:bg-accent/10 group-hover:opacity-100">
                      <Sparkles className="size-4" />
                    </button>
                  </div>
                );
              })}
            </div>

            <div className="mt-4 flex flex-wrap gap-1.5">
              {Object.entries(CATEGORY_META).map(([k, v]) => {
                const n = items.filter((m) => m.aiCategory === k).length;
                if (!n) return null;
                return (
                  <span key={k} className="pill badge rounded-full px-2.5 py-1 text-[11.5px] font-medium" style={{ background: tint(v.color, 10), color: v.color }}>
                    {v.label} · {n}
                  </span>
                );
              })}
            </div>
          </div>
        </div>

        {/* ask + write */}
        <div className="mt-8 grid grid-cols-2 gap-3">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (ask.trim()) openAsk(ask.trim());
              setAsk('');
            }}
            className="ai-ring flex items-center gap-3 rounded-2xl bg-raised/60 px-4 py-3"
          >
            <Search className="size-4 text-accent" />
            <input value={ask} onChange={(e) => setAsk(e.target.value)} placeholder="Ask anything about your email…" className="flex-1 bg-transparent text-[14px] outline-none placeholder:text-faint" />
          </form>
          <button onClick={() => composeWithAi()} className="glass flex items-center gap-3 rounded-2xl px-4 py-3 text-left transition hover:border-line-strong">
            <Sparkles className="size-4 text-accent" />
            <span className="flex-1 text-[14px] text-muted">Describe an email and AI writes it…</span>
            <ArrowRight className="size-4 text-faint" />
          </button>
        </div>
      </div>
    </div>
  );
}
